import { coreServicesApiClient, type RequestProps } from '@protector/api-client';
import type { UUID } from '@protector/utils/types';
import { fieldWithCropzonePageableSchema, fieldsBySeasonsSyncResponseSchema } from './field-cropzone.schemas.ts';

function requestWithDomain<I, O>(args: Omit<RequestProps<I, O>, 'domain'>) {
  return coreServicesApiClient.request<I, O>({ ...args, domain: 'Region' });
}

interface GetFieldsWithCropzonesParams {
  propertyId: UUID;
  seasonIds: UUID[];
  includeGeometry?: boolean;
  includeArchived?: boolean;
  seasonReferenceDate?: string;
}

export function getFieldsWithCropzones({
  propertyId,
  seasonIds,
  includeGeometry = true,
  includeArchived = false,
  seasonReferenceDate
}: GetFieldsWithCropzonesParams) {
  const params = new URLSearchParams();
  if (includeGeometry) params.append('attributes', 'geometry');
  if (includeArchived) params.append('include_archived', 'true');
  if (seasonReferenceDate) params.append('season_reference_date', seasonReferenceDate);

  return requestWithDomain({
    config: {
      method: 'POST',
      url: `/v2/properties/${propertyId}/fields/by-seasons`,
      params,
      data: { season_ids: seasonIds },
      headers: { 'client-id': 'strix-ui' }
    },
    outputSchema: fieldWithCropzonePageableSchema
  });
}

interface GetFieldsWithCropzonesSyncParams {
  propertyId: UUID;
  seasonIds: UUID[];
  updatedSince: string;
  includeGeometry?: boolean;
  includeArchived?: boolean;
  seasonReferenceDate?: string;
}

/** Backend returns 400 EARG001 if `updatedSince` is omitted. */
export function getFieldsWithCropzonesSync({
  propertyId,
  seasonIds,
  updatedSince,
  includeGeometry = true,
  includeArchived = false,
  seasonReferenceDate
}: GetFieldsWithCropzonesSyncParams) {
  const params = new URLSearchParams({ updated_since: updatedSince });
  if (includeGeometry) params.append('attributes', 'geometry');
  if (includeArchived) params.append('include_archived', 'true');
  if (seasonReferenceDate) params.append('season_reference_date', seasonReferenceDate);

  return requestWithDomain({
    config: {
      method: 'POST',
      url: `/v2/properties/${propertyId}/fields/by-seasons/sync`,
      params,
      data: { season_ids: seasonIds },
      headers: { 'client-id': 'strix-ui' }
    },
    outputSchema: fieldsBySeasonsSyncResponseSchema
  });
}
